import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { BookOpen, Plus, Edit, Trash2, Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStrategies = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'Strategy',
    description: '',
    content: '',
    image_url: '',
    pdf_url: '',
    source_url: '',
    tags: '',
    difficulty: 'Intermediate',
    is_featured: false
  });

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/betting-strategies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(response.data);
    } catch (error) {
      console.error('Error fetching strategies:', error);
      toast.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      if (editingStrategy) {
        await axios.put(`${API}/betting-strategies/${editingStrategy.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Strategy updated successfully!');
      } else {
        await axios.post(`${API}/betting-strategies`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Strategy created successfully!');
      }

      setDialogOpen(false);
      resetForm();
      fetchStrategies();
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast.error('Failed to save strategy');
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const token = localStorage.getItem('session_token');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, [field]: response.data.url });
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (strategyId) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/betting-strategies/${strategyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Strategy deleted successfully');
      fetchStrategies();
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error('Failed to delete strategy');
    }
  };

  const openEditDialog = (strategy) => {
    setEditingStrategy(strategy);
    setFormData({
      title: strategy.title,
      category: strategy.category,
      description: strategy.description,
      content: strategy.content,
      image_url: strategy.image_url || '',
      pdf_url: strategy.pdf_url || '',
      source_url: strategy.source_url || '',
      tags: strategy.tags ? strategy.tags.join(', ') : '',
      difficulty: strategy.difficulty,
      is_featured: strategy.is_featured
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingStrategy(null);
    setFormData({
      title: '',
      category: 'Strategy',
      description: '',
      content: '',
      image_url: '',
      pdf_url: '',
      source_url: '',
      tags: '',
      difficulty: 'Intermediate',
      is_featured: false
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading strategies...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <BookOpen className="w-10 h-10 text-blue-400 mr-4" />
              <div>
                <h1 className="text-3xl font-bold text-white">Manage Strategies</h1>
                <p className="text-gray-400">Create and manage betting strategies content</p>
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Strategy
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingStrategy ? 'Edit Strategy' : 'Create New Strategy'}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Strategy">Strategy</SelectItem>
                          <SelectItem value="System">System</SelectItem>
                          <SelectItem value="Method">Method</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      rows={2}
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-white">Content * (Supports HTML)</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      rows={8}
                      placeholder="Enter content here. You can use HTML tags for formatting."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Difficulty</Label>
                      <Select value={formData.difficulty} onValueChange={(value) => setFormData({ ...formData, difficulty: value })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Beginner">Beginner</SelectItem>
                          <SelectItem value="Intermediate">Intermediate</SelectItem>
                          <SelectItem value="Advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                          className="mr-2 w-4 h-4"
                        />
                        Featured Strategy
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Tags (comma separated)</Label>
                    <Input
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="e.g., value betting, bankroll, arbitrage"
                    />
                  </div>

                  <div>
                    <Label className="text-white">Image URL or Upload</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="bg-white/5 border-white/10 text-white flex-1"
                        placeholder="https://example.com/image.jpg"
                      />
                      <Button type="button" onClick={() => document.getElementById('image-upload').click()} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'image_url')}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">PDF File Upload</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={formData.pdf_url}
                        readOnly
                        className="bg-white/5 border-white/10 text-white flex-1"
                        placeholder="Upload a PDF file"
                      />
                      <Button type="button" onClick={() => document.getElementById('pdf-upload').click()} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload PDF'}
                      </Button>
                      <input
                        id="pdf-upload"
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'pdf_url')}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Source URL (for scraped content)</Label>
                    <Input
                      value={formData.source_url}
                      onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="https://example.com/original-article"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                      {editingStrategy ? 'Update Strategy' : 'Create Strategy'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {strategies.length === 0 ? (
            <Card className="glass-dark border-white/10">
              <CardContent className="pt-6 text-center py-16">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4 opacity-50" />
                <p className="text-gray-400">No strategies created yet. Click "Add Strategy" to create your first one.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {strategies.map((strategy) => (
                <Card key={strategy.id} className="glass-dark border-white/10">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{strategy.title}</h3>
                          <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            {strategy.category}
                          </span>
                          <span className="text-sm text-gray-400">{strategy.difficulty}</span>
                          {strategy.is_featured && (
                            <span className="text-xs text-yellow-400">⭐ Featured</span>
                          )}
                        </div>
                        <p className="text-gray-300 mb-2">{strategy.description}</p>
                        {strategy.tags && strategy.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {strategy.tags.map((tag, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="text-sm text-gray-500">
                          Created: {new Date(strategy.created_at).toLocaleDateString()} | 
                          Updated: {new Date(strategy.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(strategy)}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(strategy.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminStrategies;
